/**
 * Strategic Posture panel — 9 military theater monitoring cards.
 *
 * For each theater region: counts military flights and vessel events
 * within theater bounds, checks for surges, and displays activity level.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { THEATER_REGIONS } from '@/config/military';
import type { TheaterRegion } from '@/config/military';
import type { NormalizedEvent } from '@/sources/base-source';
import type { SurgeEvent } from '@/intelligence/analysis/types';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { filterEventsByTimeRange } from '@/utils/time-filter';

interface TheaterStats {
  flights: number;
  vessels: number;
  otherMilitary: number;
  hasSurge: boolean;
}

function getActivityLevel(stats: TheaterStats): 'Normal' | 'Elevated' | 'High' {
  const total = stats.flights + stats.vessels + stats.otherMilitary;
  if (stats.hasSurge || total >= 50) return 'High';
  if (total >= 15) return 'Elevated';
  return 'Normal';
}

function getActivityColor(level: 'Normal' | 'Elevated' | 'High'): string {
  switch (level) {
    case 'High': return 'var(--severity-critical)';
    case 'Elevated': return 'var(--severity-medium)';
    case 'Normal': return 'var(--severity-low)';
  }
}

function isInBounds(lat: number, lng: number, bounds: TheaterRegion['bounds']): boolean {
  const inLat = lat >= bounds.south && lat <= bounds.north;
  const inLng = bounds.west <= bounds.east
    ? lng >= bounds.west && lng <= bounds.east
    : lng >= bounds.west || lng <= bounds.east;
  return inLat && inLng;
}

export function createStrategicPosturePanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'strategic-posture-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function computeTheaterStats(theater: TheaterRegion, events: NormalizedEvent[], surges: SurgeEvent[]): TheaterStats {
    let flights = 0;
    let vessels = 0;
    let otherMilitary = 0;

    for (const ev of events) {
      if (!ev.location) continue;
      if (!isInBounds(ev.location.lat, ev.location.lng, theater.bounds)) continue;

      if (ev.type === 'flight_position') flights++;
      else if (ev.type === 'vessel_position') vessels++;
      else if (ev.type === 'military') otherMilitary++;
    }

    // Check if any surge is in this theater's region
    const hasSurge = surges.some(s => {
      const regionLower = s.region.toLowerCase();
      return regionLower.includes(theater.name.toLowerCase()) ||
        regionLower.includes(theater.id);
    });

    return { flights, vessels, otherMilitary, hasSurge };
  }

  function render(): void {
    const state = appStore.getState();
    const events = filterEventsByTimeRange(state.events, state.timeRange);
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();
    const surges = result?.surges ?? [];

    contentArea.textContent = '';

    const grid = document.createElement('div');
    grid.style.cssText = 'display: grid; grid-template-columns: 1fr; gap: 8px;';

    for (const theater of THEATER_REGIONS) {
      const stats = computeTheaterStats(theater, events, surges);
      const level = getActivityLevel(stats);
      const color = getActivityColor(level);

      const card = document.createElement('div');
      card.style.cssText = `
        padding: 10px 12px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px solid var(--border-primary);
      `;

      // Header: name + activity badge
      const headerRow = document.createElement('div');
      headerRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

      const name = document.createElement('span');
      name.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
      name.textContent = theater.name;
      headerRow.appendChild(name);

      const badge = document.createElement('span');
      badge.style.cssText = `
        font-size: 9px; font-weight: 600; text-transform: uppercase;
        padding: 1px 6px; border-radius: 3px; margin-left: auto;
        background: ${color}22; color: ${color};
      `;
      badge.textContent = level;
      headerRow.appendChild(badge);

      // Priority indicator
      const priorityDot = document.createElement('span');
      priorityDot.style.cssText = 'font-size: 9px; color: var(--text-muted);';
      priorityDot.textContent = `P${theater.priority}`;
      priorityDot.title = `Priority ${theater.priority}`;
      headerRow.appendChild(priorityDot);

      card.appendChild(headerRow);

      // Stats row
      const statsRow = document.createElement('div');
      statsRow.style.cssText = 'display: flex; gap: 12px; font-size: 11px; color: var(--text-secondary);';

      const flightStat = document.createElement('span');
      flightStat.textContent = `\u2708 ${stats.flights}`;
      flightStat.title = 'Military flights';
      statsRow.appendChild(flightStat);

      const vesselStat = document.createElement('span');
      vesselStat.textContent = `\u2693 ${stats.vessels}`;
      vesselStat.title = 'Vessel events';
      statsRow.appendChild(vesselStat);

      if (stats.otherMilitary > 0) {
        const milStat = document.createElement('span');
        milStat.textContent = `\u2B50 ${stats.otherMilitary}`;
        milStat.title = 'Other military events';
        statsRow.appendChild(milStat);
      }

      if (stats.hasSurge) {
        const surgeLabel = document.createElement('span');
        surgeLabel.style.cssText = 'color: var(--severity-critical); font-weight: 600;';
        surgeLabel.textContent = 'SURGE';
        statsRow.appendChild(surgeLabel);
      }

      card.appendChild(statsRow);

      // Description
      const desc = document.createElement('div');
      desc.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 4px;';
      desc.textContent = theater.description;
      card.appendChild(desc);

      grid.appendChild(card);
    }

    contentArea.appendChild(grid);
  }

  // Initial render
  render();

  // Subscribe to analysis results and event changes
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.timeRange,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
